import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Band, Column, Grid as GridDoc, Mark, Row } from '../data';
import { fmtLong, fmtShort, leaderShort, leaderWords, monthLabel, outcomeWords, pinnedLeader } from '../format';
import { navigate } from '../router';
import { GLYPH, KEY_DEF, KEY_ORDER, KEY_SHORT, NO_ROLL_CALL } from './marks';
import './grid.css';

// The design's geometry (README, "Grid geometry"). Column width never varies, so cells
// stay comparable across terms.
export const COLW = 30;
export const HDRH = 150;

// The measurements that change on a phone (README section 11, and the prototype's phone
// view): a 182px name column showing last name and seat, 44px rows and a 44px month ruler
// so a name, a header and a month are real tap targets, and no minimum width, since the
// hatched field beside a short term is a desktop affordance. Everything else is the same grid.
// toggleH is a "Left office this term" row including its bottom rule: tappable, so 44px tall
// on a phone like everything else a finger presses.
export interface Geometry { nameW: number; rowH: number; rulerH: number; minW: number; toggleH: number }
export const DESKTOP: Geometry = { nameW: 300, rowH: 32, rulerH: 24, minW: 1080, toggleH: 27 };
export const PHONE: Geometry = { nameW: 182, rowH: 44, rulerH: 44, minW: 0, toggleH: 45 };
export const BANDH = 28;    // a band label row, including its top rule
const WINDOW_Y = 800;       // rows drawn beyond the viewport, each way (the design's figure)
const WINDOW_X = 600;       // columns drawn beyond the viewport, each way
const STEP_Y = 200;         // re-window when the page has scrolled this far
const STEP_X = 300;

export type Line =
  | { kind: 'band'; key: string; label: string; count: string; pinned?: boolean; y: number; h: number }
  | { kind: 'toggle'; key: string; band: Band; count: number; open: boolean; y: number; h: number }
  | { kind: 'member'; key: string; r: number; row: Row; left: boolean; pinned?: boolean; y: number; h: number };

/** "Your representatives" (README, "Row structure", 1): only after a lookup, above the White
 *  House. The rows are indexes into grid.rows, already in order; they stay in their own
 *  chamber's band too, as the design draws them. `count` is the band's right-hand label. */
export interface PinnedBand { rows: number[]; count: string }

// A line before its position is known (Omit on a union, taken member by member).
type Unplaced<T> = T extends unknown ? Omit<T, 'y'> : never;

export interface GridProps {
  grid: GridDoc;
  columns: number[];                 // indexes into grid.columns, in display order
  rowFilter: (row: Row) => boolean;
  openLeft: Record<Band, boolean>;
  onToggleLeft: (band: Band) => void;
  selectedItem: string | null;
  phone: boolean;
  pinned: PinnedBand | null;
  sortBy?: SortBy;
}

const BAND_LABEL: Record<Band, string> = { executive: 'White House', senate: 'Senate', house: 'House' };

/** How the people in each band are ordered: by time in office, the grid file's own order and the site's default,
 *  or by name (Dan, 16 Oct 2026: "Let's keep tenure as our default, but add the option for people that want to
 *  sort by name"). Not remembered between visits: Privacy promises only the district is kept. */
export type SortBy = 'tenure' | 'name';
const BY_NAME = new Intl.Collator('en', { sensitivity: 'base' });

/** The order of a band's rows: its pinned leader first (Dan, 24 Sep 2026: "Leadership hierarchy is important"),
 *  then by time in office (the file's order) or by surname, then given name. Every list of people uses it. */
export function rowCompare(grid: GridDoc, by: SortBy): (a: number, b: number) => number {
  const lead = (r: number) => (pinnedLeader(grid.rows[r].leader, grid.rows[r].left) ? 0 : 1);
  const name = (a: number, b: number) =>
    BY_NAME.compare(grid.rows[a].last, grid.rows[b].last) || BY_NAME.compare(grid.rows[a].first, grid.rows[b].first);
  return (a, b) => lead(a) - lead(b) || (by === 'name' ? name(a, b) : 0) || a - b;
}

/** The part of the page actually on screen. NOT window.innerWidth/innerHeight: a phone reports
 *  those as its fully zoomed-out size (1500 x 3248 on a 375 x 812 screen, measured), which would
 *  draw several times the cells anyone can see. The visual viewport is what is on screen, at any
 *  pinch zoom. */
function onScreen() {
  const vv = window.visualViewport, de = document.documentElement;
  return vv ? { x: vv.pageLeft, y: vv.pageTop, w: vv.width, h: vv.height }
            : { x: window.scrollX, y: window.scrollY, w: de.clientWidth, h: de.clientHeight };
}

/** The grid's lines, in order: "Your representatives" after a lookup, then each band's pinned
 *  leader, its members in time-in-office order and its collapsed "left office" group, each with
 *  its place on the page. Shared by the desktop grid and the phone's paged grid (PhoneGrid.tsx). */
export function layoutLines(grid: GridDoc, rowFilter: (row: Row) => boolean, openLeft: Record<Band, boolean>,
                            G: Geometry, pinned: PinnedBand | null, by: SortBy = 'tenure'): { out: Line[]; height: number } {
  const out: Line[] = [];
  let y = 0;
  const push = (l: Unplaced<Line>) => { const withY = { ...l, y } as Line; out.push(withY); y += l.h; };
  const mine = pinned ? pinned.rows.filter((r) => rowFilter(grid.rows[r])) : [];
  if (pinned && mine.length) {
    push({ kind: 'band', key: 'b-pinned', label: 'Your representatives', count: pinned.count, pinned: true, h: BANDH });
    for (const r of mine) {
      push({ kind: 'member', key: 'p-' + r, r, row: grid.rows[r], left: grid.rows[r].left, pinned: true, h: G.rowH });
    }
  }
  const bands: Band[] = ['executive', 'senate', 'house'];
  for (const band of bands) {
    const serving: number[] = [];
    const left: number[] = [];
    grid.rows.forEach((row, r) => {
      if (row.band !== band || !rowFilter(row)) return;
      (row.left ? left : serving).push(r);
    });
    // The Speaker leads the House and the Majority Leader the Senate, marked; everyone else by time in office
    // (the file's order) or by name (rowCompare).
    const order = rowCompare(grid, by);
    serving.sort(order);
    left.sort(order);
    const total = grid.rows.filter((row) => row.band === band && !row.left).length;
    const shown = serving.length;
    if (!shown && !left.length) continue;
    push({ kind: 'band', key: 'b-' + band, label: BAND_LABEL[band],
           count: shown === total ? String(total) : `${shown} of ${total}`, h: BANDH });
    for (const r of serving) push({ kind: 'member', key: 'm-' + r, r, row: grid.rows[r], left: false, h: G.rowH });
    if (left.length) {
      const open = openLeft[band];
      push({ kind: 'toggle', key: 't-' + band, band, count: left.length, open, h: G.toggleH });
      if (open) for (const r of left) push({ kind: 'member', key: 'm-' + r, r, row: grid.rows[r], left: true, h: G.rowH });
    }
  }
  return { out, height: y };
}

export function Grid({ grid, columns, rowFilter, openLeft, onToggleLeft, selectedItem, phone, pinned, sortBy = 'tenure' }: GridProps) {
  const G = phone ? PHONE : DESKTOP;
  const contentRef = useRef<HTMLDivElement>(null);
  const colOv = useRef<HTMLDivElement>(null);
  const rowOv = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(onScreen);
  const last = useRef({ x: -1e9, y: -1e9 });

  const lines = useMemo(() => layoutLines(grid, rowFilter, openLeft, G, pinned, sortBy),
    [grid, rowFilter, openLeft, G, pinned, sortBy]);

  // ---- scrolling: re-window only when the page has moved far enough ----------------
  useEffect(() => {
    const onScroll = () => {
      const v = onScreen();
      if (Math.abs(v.y - last.current.y) < STEP_Y && Math.abs(v.x - last.current.x) < STEP_X) return;
      last.current = { x: v.x, y: v.y };
      // Not gated behind requestAnimationFrame: that is throttled in a background frame.
      setView(v);
    };
    const onResize = () => { last.current = { x: -1e9, y: -1e9 }; onScroll(); };
    const vv = window.visualViewport;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    vv?.addEventListener('scroll', onScroll);          // a pinch zoom moves the view, not the page
    vv?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize);
      vv?.removeEventListener('scroll', onScroll); vv?.removeEventListener('resize', onResize);
    };
  }, []);

  // ---- the crosshair and the one shared tooltip, moved imperatively ----------------
  // The grid is thousands of nodes; a re-render per mouse move is not viable.
  useEffect(() => {
    const tip = document.getElementById('sr-tip');
    const onMove = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      const cell = t?.closest?.('[data-r]') as HTMLElement | null;
      const head = t?.closest?.('[data-col]') as HTMLElement | null;
      const defEl = t?.closest?.('[data-def]') as HTMLElement | null;
      const content = contentRef.current;
      let text = '';
      if (cell && content && colOv.current && rowOv.current) {
        const base = content.getBoundingClientRect(), r = cell.getBoundingClientRect();
        Object.assign(colOv.current.style, { display: 'block', left: r.left - base.left + 'px', width: r.width + 'px' });
        Object.assign(rowOv.current.style, { display: 'block', top: r.top - base.top + 'px', height: r.height + 'px' });
        const row = grid.rows[+cell.dataset.r!], col = grid.columns[+cell.dataset.c!];
        const mark = grid.cells[+cell.dataset.r!][+cell.dataset.c!] as Mark;
        if (row && col) text = cellTooltip(row, col, mark, grid.legend[mark]);
      } else if (head && content && colOv.current) {
        // A column heading: its whole title, which the heading can only show cut short, and its column lit.
        const base = content.getBoundingClientRect(), r = head.getBoundingClientRect();
        Object.assign(colOv.current.style, { display: 'block', left: r.left - base.left + 'px', width: r.width + 'px' });
        if (rowOv.current) rowOv.current.style.display = 'none';
        const col = grid.columns[+head.dataset.col!];
        if (col) text = headTooltip(col);
      } else {
        if (colOv.current) colOv.current.style.display = 'none';
        if (rowOv.current) rowOv.current.style.display = 'none';
      }
      if (defEl?.dataset.def) text = defEl.dataset.def;       // an empty definition is no definition
      if (!tip) return;
      if (!text) { tip.style.display = 'none'; return; }
      tip.textContent = text;
      tip.style.display = 'block';
      const w = tip.offsetWidth, h = tip.offsetHeight;
      tip.style.left = Math.max(4, Math.min(e.clientX + 14, document.documentElement.clientWidth - w - 10)) + 'px';
      tip.style.top = (e.clientY - h - 12 < 8 ? e.clientY + 18 : e.clientY - h - 12) + 'px';
    };
    const hide = () => {
      if (colOv.current) colOv.current.style.display = 'none';
      if (rowOv.current) rowOv.current.style.display = 'none';
      if (tip) tip.style.display = 'none';
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseleave', hide, true);
    return () => { document.removeEventListener('mousemove', onMove, true); document.removeEventListener('mouseleave', hide, true); };
  }, [grid]);

  // ---- which rows and columns to draw --------------------------------------------
  const [origin, setOrigin] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // the table's own 1px border, then the header block (its borders sit inside its height)
    const top = r.top + window.scrollY + 1 + G.rulerH + HDRH;
    const left = r.left + window.scrollX + G.nameW;
    if (Math.abs(top - origin.top) > 1 || Math.abs(left - origin.left) > 1) setOrigin({ top, left });
  });

  const colsW = columns.length * COLW;
  const contentW = Math.max(G.nameW + colsW, G.minW);
  const y0 = view.y - origin.top - WINDOW_Y, y1 = view.y - origin.top + view.h + WINDOW_Y;
  const x0 = view.x - origin.left - WINDOW_X, x1 = view.x - origin.left + view.w + WINDOW_X;
  const c0 = Math.max(0, Math.floor(x0 / COLW));
  const c1 = Math.min(columns.length, Math.ceil(x1 / COLW));
  const visibleCols = columns.slice(c0, c1);
  const lead = c0 * COLW;

  // ROWS ARE WINDOWED AS COLUMNS ARE: a member row more than WINDOW_Y off screen is not drawn at
  // all, and each run of them is one spacer of the same height, so the page keeps its size and
  // every row its place. Until 22 Sep 2026 every row drew its name, pinned to the left edge, and on
  // a mid-tier phone profile (tools/phone-scroll.mjs, 11x) repainting 553 pinned names dropped
  // about half the frames of a swipe. Band labels and "left office" rows are few and always drawn.
  type Gap = { kind: 'gap'; key: string; h: number };
  const drawn: (Line | Gap)[] = [];
  for (const l of lines.out) {
    if (l.kind === 'member' && !(l.y + l.h > y0 && l.y < y1)) {
      const g = drawn[drawn.length - 1];
      if (g && g.kind === 'gap') g.h += l.h;
      else drawn.push({ kind: 'gap', key: 'gap-' + l.key, h: l.h });
    } else {
      drawn.push(l);
    }
  }

  // the month ruler: one button per run of columns in the same month
  const ruler = useMemo(() => {
    const groups: { label: string; n: number; start: number }[] = [];
    columns.forEach((ci, i) => {
      const label = monthLabel(grid.columns[ci].date);
      const g = groups[groups.length - 1];
      if (g && g.label === label) g.n++; else groups.push({ label, n: 1, start: i });
    });
    return groups;
  }, [grid, columns]);

  const tenureScale = grid.tenure_scale_years || 1;

  return (
    <div class="sr-grid" data-sr-grid="1">
      <div ref={contentRef} class="sr-content" style={{ width: contentW + 'px' }}>
        <div ref={colOv} class="sr-cross sr-cross-col" />
        <div ref={rowOv} class="sr-cross sr-cross-row" />
        {contentW > G.nameW + colsW && (
          <div class="sr-empty-field" style={{ top: G.rulerH + HDRH + 'px', left: G.nameW + colsW + 'px' }} />
        )}

        <div class="sr-head">
          <div class="sr-corner" style={{ height: G.rulerH + HDRH + 'px' }}>
            {/* On a phone the key is behind the Key button above the grid (README section 11). */}
            {!phone && <CellKey legend={grid.legend} />}
            <div class="sr-corner-foot"><span>Member</span><span>{phone ? 'Yrs' : 'Time in office'}</span></div>
          </div>
          <div style={{ width: colsW + 'px' }}>
            <div class="sr-ruler" style={{ height: G.rulerH + 'px' }}>
              {ruler.map((g) => (
                <button key={g.label + g.start} class="sr-month" style={{ width: g.n * COLW + 'px' }}
                        onClick={() => window.scrollTo({ left: origin.left - G.nameW + g.start * COLW })}>
                  {g.n === 1 ? g.label.slice(0, 3) : g.label}
                </button>
              ))}
            </div>
            <div class="sr-heads" style={{ height: HDRH + 'px' }}>
              <div style={{ width: lead + 'px', flex: 'none' }} />
              {visibleCols.map((ci) => {
                const col = grid.columns[ci];
                return (
                  <div key={col.id} class={'sr-head-cell' + (selectedItem === col.id ? ' is-selected' : '')} data-col={ci}
                       {...pressable(() => navigate('/item/' + col.id))}
                       aria-label={`${col.number}${col.title ? ': ' + col.title : ''}`}>
                    <div class="sr-head-vert">
                      <span class="sr-head-num">{col.number}</span>
                      <span class="sr-head-title">{col.title}</span>
                    </div>
                    <div class="sr-head-date">{fmtShort(col.date)}</div>
                    <div class={'sr-rule o-' + col.outcome} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {drawn.map((l) => {
          if (l.kind === 'gap') return <div key={l.key} class="sr-gap" style={{ height: l.h + 'px' }} />;
          if (l.kind === 'band') {
            return (
              <div key={l.key} class={'sr-band' + (l.pinned ? ' is-pinned' : '')} style={{ top: G.rulerH + HDRH + 'px' }}>
                <div class="sr-band-label">
                  <span class="sr-band-name">{l.label}</span>
                  <span class="sr-band-count">{l.count}</span>
                </div>
              </div>
            );
          }
          if (l.kind === 'toggle') {
            return (
              <div key={l.key} class="sr-toggle" style={{ height: G.toggleH - 1 + 'px' }}>
                <div class="sr-toggle-label" onClick={() => onToggleLeft(l.band)}>
                  {/* the phone's 182px column takes the design's shorter label */}
                  <span class="sr-toggle-name">{phone ? 'Left office' : 'Left office this term'}</span>
                  <span class="sr-toggle-hint">{l.count} {l.count === 1 ? 'member' : 'members'} {l.open ? '▾' : '▸'}</span>
                </div>
              </div>
            );
          }
          const line = grid.cells[l.r];
          const row = l.row;
          return (
            <div key={l.key} class={'sr-row' + (l.pinned ? ' is-pinned' : '')
              + (!l.pinned && pinnedLeader(row.leader, row.left) ? ' is-leader' : '')}>
              <NameCell row={row} left={l.left} scale={tenureScale} phone={phone} />
              <div style={{ width: lead + 'px', flex: 'none' }} />
              {visibleCols.map((ci) => {
                const m = line[ci] as Mark;
                return <div key={ci} data-r={l.r} data-c={ci} class={'sr-cell c-' + cls(m)}>{GLYPH[m]}</div>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The cell key: every mark, its short name, and a definition on the ones that need it. In
 *  the grid's corner on a desktop; behind the Key button on a phone. */
export function CellKey({ legend }: { legend: Record<Mark, string> }) {
  return (
    <div class="sr-corner-key">
      {KEY_ORDER.map((m) => (
        <span class="sr-key-item" key={m}>
          <span class={'sr-cell sr-key-cell c-' + cls(m)}>{GLYPH[m]}</span>
          <span class={'sr-key-label' + (KEY_DEF[m] ? ' has-def' : '')} data-def={KEY_DEF[m] || ''}>
            {KEY_SHORT[m] || legend[m]}
          </span>
        </span>
      ))}
    </div>
  );
}

function NameCell({ row, left, scale, phone }: { row: Row; left: boolean; scale: number; phone: boolean }) {
  const exec = row.role === 'president' || row.role === 'vp';
  const initial = /^[A-Z]\./.test(row.first) ? row.first : row.first.charAt(0) + '.';
  const office = row.role === 'president' ? 'Pres' : 'VP';
  // The phone's 182px column holds the last name and the seat, as the design draws it; the
  // party letter and the tenure bar return at desktop width. The member card has both.
  const ps = phone ? (exec ? office : row.seat ?? '')
    : exec ? `${row.party ?? ''} · ${office}` : `${row.party ?? ''} · ${row.seat ?? ''}`;
  const pct = Math.max(2, Math.round((row.tenure_days / 365.2425 / scale) * 100));
  const firstYear = row.first_sworn ? row.first_sworn.slice(0, 4) : '';
  const def = `${row.full} — service began ${firstYear} · ${row.years} ${row.years === 1 ? 'year' : 'years'}`
    + ' actually served in Congress and the White House'
    + (row.gap ? ' · service not continuous; gaps are not counted' : '')
    + (row.leader ? ' · ' + leaderWords(row.leader) : '');
  return (
    <div class={'sr-name' + (left ? ' is-left' : '')} data-def={def}
         {...pressable(() => navigate('/member/' + row.id))} aria-label={`${row.full}: member card`}>
      <span class={'sr-name-text' + (row.leader ? ' has-role' : '')}>
        {phone ? row.last : `${row.last}, ${initial}`}
        {row.leader && <span class="sr-name-role">{leaderShort(row.leader)}</span>}
      </span>
      <span class="sr-name-ps">{ps}</span>
      {!phone && <span class="sr-bar"><span style={{ width: pct + '%' }} /></span>}
      <span class="sr-name-yrs">
        {left && row.left_on ? (phone ? '' : 'left ') + fmtShort(row.left_on)
          : phone ? String(row.years) : `${row.years} ${row.years === 1 ? 'yr' : 'yrs'}`}
      </span>
    </div>
  );
}

/** The CSS class name for a mark (some mark characters are not valid in a class). */
/** A div that acts as a button: reachable by Tab, pressed by Enter or Space. The grid's
 *  cells stay divs because a <button> cannot hold its vertical-text layout. */
export function pressable(go: () => void) {
  return {
    role: 'button' as const, tabIndex: 0, onClick: go,
    onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } },
  };
}

export function cls(m: Mark): string {
  return ({ '-': 'nv', '~': 'voice', '.': 'pend', '?': 'unk', x: 'na', o: 'nio', s: 'spk' } as Record<string, string>)[m] ?? m;
}

/** One mark (~) for every way a chamber passes an item without a roll call; this names the way,
 *  as the record words it (marks.ts NO_ROLL_CALL), or null to fall back on the key's label. */
/** A senator's mark on an impeachment trial's verdict, in the trial's own words (Dan, 15 Oct 2026). */
export function trialWord(col: Column, role: Row['role'], mark: Mark): string | null {
  if (col.trial !== 'senate' || role !== 'sen') return null;
  return mark === 'Y' ? 'Guilty' : mark === 'N' ? 'Not guilty' : null;
}

export function passedHow(col: Column, role: Row['role'], mark: Mark): string | null {
  const how = mark === '~' ? col.how?.[role === 'rep' ? 'house' : 'senate'] : null;
  return how && NO_ROLL_CALL[how] ? NO_ROLL_CALL[how].short : null;
}

/** The day a row's mark refers to: its own chamber's or office's, never `col.date`, the latest
 *  roll call, which may be the other chamber's and is never the President's (until 23 Sep 2026
 *  the hover and the member card showed it on every row: Biden "Signed · 22 May 2023" on H.R.
 *  1076, signed 13 Sep 2024). None for Not applicable, or an office with no day (pending). Files
 *  built before then carry no `dates`, and get the old day. */
export function officeDay(col: Column, role: Row['role'], mark: Mark): string | null {
  if (mark === 'x') return null;
  if (!col.dates) return col.date;
  return col.dates[role === 'president' ? 'president' : role === 'rep' ? 'house' : 'senate'];
}

/** A column heading's hover (Dan, 29 Sep 2026: "so the names of each item are easier to read"): its
 *  number, its whole title, its outcome and that outcome's day, dated as the share card dates it
 *  (worker/sr/cards._outcome_date): a law or a veto on the President's day, a law made over a veto
 *  with none (the record names no one day), anything else on its final vote, the later chamber's. */
export function headTooltip(col: Column): string {
  const day = outcomeDay(col);
  return [col.number, col.title, outcomeWords(col.outcome, col.kind), day ? fmtLong(day) : null]
    .filter(Boolean).join(' · ');
}

/** The day an item's outcome refers to (see headTooltip); also the phone's caption strip. */
export function outcomeDay(col: Column): string | null {
  const d = col.dates, law = col.outcome === 'became_law' || col.outcome === 'law_without_signature';
  if (law && (col.president === 'signed' || col.president === 'without_signature')) return d ? d.president : null;
  if (col.outcome === 'vetoed' && col.president === 'vetoed') return d ? d.president : null;
  if (col.outcome === 'became_law') return null;
  return (d ? [d.house, d.senate].filter(Boolean).sort().pop() : col.date) ?? null;
}

function cellTooltip(row: Row, col: Column, mark: Mark, label: string): string {
  const who = row.role === 'president' || row.role === 'vp'
    ? `${row.full} (${row.party ?? ''}, ${row.role === 'president' ? 'President' : 'Vice President'})`
    : `${row.full} (${row.party ?? ''}-${row.seat ?? ''})`;
  // The President's Pending is not a chamber's: both chambers have passed it. And a hatched
  // cell on a bill still moving is not "never goes there": it may yet reach the President.
  const what = mark === '.' && row.role === 'president' ? 'Awaiting the President’s action'
    : mark === 'x' && row.role === 'president' && col.president === 'not_yet' ? 'Not before the President yet'
    : trialWord(col, row.role, mark) ?? passedHow(col, row.role, mark) ?? (label || mark);
  const day = officeDay(col, row.role, mark);
  return `${who} · ${col.number} · ${what}${day ? ' · ' + fmtLong(day) : ''}`;
}
